/**
 * Mirror of the Python `flatten_for_engine` so the preview modal can
 * align indices between local section-walk and the server response.
 *
 * Keep this in lock-step with `src/builder/config_compat.py::flatten_for_engine`.
 */

import type {
  ChartConfig,
  DashboardRenderConfig,
  DrillConfig,
  KpiConfig,
  TableConfig,
  TextConfig,
  DrillLayout,
} from "@pervasivemind/dashboard-render";
import { DRILL_LAYOUTS } from "@pervasivemind/dashboard-render";

import type { DashboardConfigV2, DashboardFilter, DisplayLabel } from "@/types";

/**
 * Chart-table ("table"-type chart) column mapping. The authored config
 * stores the column list under ``columns`` (what the engine's
 * ``discovery._parse_charts`` reads), but the engine serializer
 * (``routes._serialize_chart``) emits it as ``table_columns`` — the key
 * the shared renderer reads (``DashboardBody`` / ``DrillSection`` →
 * ``GenericChart tableColumns``). ``flattenForRender`` must apply the
 * same rename or every chart-table card renders EMPTY in the builder
 * canvas while the portal shows it fine. ``table_columns`` wins when
 * present (some prod configs duplicate the array under that key as a
 * workaround predating this map).
 */
function chartTableColumns(props: Record<string, unknown>): unknown {
  return props.table_columns ?? props.columns;
}

export interface FlatDashboard {
  filters: DashboardFilter[];
  kpis: Array<Record<string, unknown>>;
  table: Record<string, unknown>;
  charts: Array<Record<string, unknown>>;
  drills: Array<{
    drill_id: string;
    // Iter 16: accept ``DisplayLabel`` (str | {en, es}) — the engine
    // serializer resolves to the active locale server-side.
    title?: DisplayLabel | null;
    description?: DisplayLabel | null;
    kpis: Array<Record<string, unknown>>;
    charts: Array<Record<string, unknown>>;
    table: Record<string, unknown>;
  }>;
}

export function flattenForEngine(dashboard: DashboardConfigV2 | undefined | null): FlatDashboard {
  if (!dashboard || dashboard.version !== 2) {
    return { filters: [], kpis: [], table: {}, charts: [], drills: [] };
  }

  const filters: DashboardFilter[] = [];
  const seen = new Set<string>();
  for (const filt of dashboard.global_filters ?? []) {
    if (!filt?.key || seen.has(filt.key)) continue;
    seen.add(filt.key);
    filters.push({ ...filt });
  }

  const kpis: Array<Record<string, unknown>> = [];
  let table: Record<string, unknown> = {};
  const charts: Array<Record<string, unknown>> = [];
  const drills: FlatDashboard["drills"] = [];

  for (const section of dashboard.sections ?? []) {
    // Drill sections live separately from the flat kpi/chart/table lists.
    if (section.kind === "drill" && section.drill_id) {
      const dKpis: Array<Record<string, unknown>> = [];
      const dCharts: Array<Record<string, unknown>> = [];
      let dTable: Record<string, unknown> = {};
      for (const item of section.items ?? []) {
        const props = (item.props ?? {}) as Record<string, unknown>;
        if (item.element === "kpi") {
          dKpis.push({ ...props });
        } else if (item.element === "chart") {
          dCharts.push({ ...props });
        } else if (item.element === "table" && Object.keys(dTable).length === 0) {
          dTable = { ...props };
        }
      }
      drills.push({
        drill_id: section.drill_id,
        title: section.title ?? null,
        description: section.description ?? null,
        kpis: dKpis,
        charts: dCharts,
        table: dTable,
      });
      continue;
    }
    for (const filt of section.filters ?? []) {
      if (!filt?.key || seen.has(filt.key)) continue;
      seen.add(filt.key);
      filters.push({ ...filt });
    }
    for (const item of section.items ?? []) {
      const props = (item.props ?? {}) as Record<string, unknown>;
      if (item.element === "kpi") {
        kpis.push({ ...props });
      } else if (item.element === "chart") {
        charts.push({ ...props });
      } else if (item.element === "table" && Object.keys(table).length === 0) {
        table = { ...props };
      }
    }
  }

  return { filters, kpis, table, charts, drills };
}

/**
 * Full flattened engine-shape config for the SHARED ``DashboardBody``
 * (P2.3) — the portal-native shape. Unlike ``flattenForEngine`` (which only
 * captures a single ``table`` for index alignment), this stamps every item
 * with its section metadata (``section_id``/``section_title``/
 * ``section_subtitle``/``width``/``order``) + an ``item_id`` (so the
 * Dashboard-tab inspector can select it) and collects ALL named ``tables`` +
 * ``texts``. Mirrors the Python ``config_compat.flatten_for_engine`` which
 * stamps the same fields on the server side.
 */
export function flattenForRender(
  dashboard: DashboardConfigV2 | undefined | null,
): DashboardRenderConfig {
  if (!dashboard || dashboard.version !== 2) {
    return { filters: [], kpis: [], charts: [] };
  }

  const filters: DashboardFilter[] = [];
  const seen = new Set<string>();
  for (const filt of dashboard.global_filters ?? []) {
    if (!filt?.key || seen.has(filt.key)) continue;
    seen.add(filt.key);
    filters.push({ ...filt });
  }

  const kpis: KpiConfig[] = [];
  const charts: ChartConfig[] = [];
  const tables: TableConfig[] = [];
  const texts: TextConfig[] = [];
  const drills: DrillConfig[] = [];
  let order = 0;

  for (const section of dashboard.sections ?? []) {
    if (section.kind === "drill" && section.drill_id) {
      const dKpis: DrillConfig["kpis"] = [];
      const dCharts: ChartConfig[] = [];
      for (const item of section.items ?? []) {
        const props = (item.props ?? {}) as Record<string, unknown>;
        if (item.element === "kpi") {
          dKpis.push(props as unknown as DrillConfig["kpis"][number]);
        } else if (item.element === "chart") {
          dCharts.push({
            ...props,
            table_columns: chartTableColumns(props),
          } as unknown as ChartConfig);
        }
      }
      drills.push({
        drill_id: section.drill_id,
        title: section.title ?? null,
        description: section.description ?? null,
        kpis: dKpis,
        charts: dCharts,
        // Mirror of the Python flatten's drill-section passthrough so
        // the builder Preview's row-drill panel behaves exactly like
        // the portal (nested 2nd level + composition override).
        nested_table_id: section.nested_table_id ?? null,
        nested_drill_target: section.nested_drill_target ?? null,
        nested_drill_param: section.nested_drill_param ?? null,
        // Mirror of the engine's discovery normalization: only the
        // known values (DRILL_LAYOUTS) pass through; junk (an untyped
        // JSONB write) maps to null so the panel falls back to the
        // automatic colSpan rule exactly like the portal would.
        drill_layout: (DRILL_LAYOUTS as readonly unknown[]).includes(section.drill_layout)
          ? (section.drill_layout as DrillLayout)
          : null,
      });
      continue;
    }
    for (const filt of section.filters ?? []) {
      if (!filt?.key || seen.has(filt.key)) continue;
      seen.add(filt.key);
      filters.push({ ...filt });
    }
    const sectionMeta = {
      section_id: section.id,
      section_title: section.title ?? undefined,
      section_subtitle: section.subtitle ?? undefined,
      // Mirror of the Python flatten's ``section_info`` stamp (title
      // tooltips, 2026-07) — the builder canvas shows the same ⓘ the
      // portal will.
      section_info: section.info ?? undefined,
    };
    for (const item of section.items ?? []) {
      const props = (item.props ?? {}) as Record<string, unknown>;
      // Uniform grid — stamp the RAW authored ``height`` (no default):
      // the renderer resolves absent heights with the mirrored
      // DEFAULT_H table + the legacy row_span mapping
      // (``grid-layout.resolveHeight`` — the same rule as Python's
      // ``config_compat.legacy_height``). Python's ``_stamp`` uses
      // ``setdefault`` — a pre-existing props value WINS over the item
      // field — for ALL THREE layout fields; mirrored here for width /
      // row_span / height alike (review #14): props wins when it holds
      // a valid number, else the item field. (Python's setdefault keeps
      // ANY props value including junk; junk is rejected at publish, so
      // the valid-number gate only diverges on configs that can't
      // publish.)
      const layoutNumber = (v: unknown): number | undefined =>
        typeof v === "number" && Number.isFinite(v) ? v : undefined;
      const propsWidth = layoutNumber(props.width);
      const propsRowSpan = layoutNumber(props.row_span);
      const propsHeight = layoutNumber(props.height);
      const base = {
        ...props,
        ...sectionMeta,
        width: propsWidth ?? item.width,
        // Mirrors the Python flattener's item-level ``row_span`` stamp
        // (default 1 = single row) so the builder Preview places
        // spanned items exactly like the portal.
        row_span: propsRowSpan ?? item.row_span ?? 1,
        height: propsHeight ?? item.height,
        order: order++,
        item_id: item.id,
      };
      if (item.element === "kpi") {
        kpis.push(base as unknown as KpiConfig);
      } else if (item.element === "chart") {
        charts.push({
          ...base,
          table_columns: chartTableColumns(props),
        } as unknown as ChartConfig);
      } else if (item.element === "table") {
        tables.push({ ...base, id: item.id } as unknown as TableConfig);
      } else if (item.element === "text") {
        texts.push(base as unknown as TextConfig);
      }
    }
  }

  return {
    filters,
    kpis,
    charts,
    tables,
    texts,
    drills,
    // Glossary drawer + scope strip: the Preview renders them exactly
    // like the portal (same DashboardBody).
    glossary: dashboard.glossary ?? null,
    // ``true`` or a list of filter keys (D36); validated at publish.
  };
}
